<template>
  <v-dialog :value="value" max-width="850" scrollable :persistent="saving" @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="subtitle-1">{{ $t('personInfo.merge.title') }}</v-card-title>
      <v-card-text>
        <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>
        <template v-if="!preview">
          <v-autocomplete v-model="otherId" :items="candidates" item-value="id" item-text="label"
            :label="$t('personInfo.merge.other')" outlined dense clearable :disabled="loading" @change="loadOther" />
          <v-progress-linear v-if="loading" indeterminate />
          <template v-if="other && !loading">
            <h3 class="subtitle-1">{{ $t('personInfo.merge.keepData') }}</h3>
            <v-radio-group v-model="keepId" class="mt-2">
              <v-radio v-for="entry in [person, other]" :key="entry.id" :value="entry.id">
                <template v-slot:label>
                  <div class="merge-option"><strong>{{ name(entry) }} <span class="font-weight-regular">#{{ entry.id }}</span></strong><span>{{ (entry.emails || []).join(', ') || $t('personInfo.noEmail') }}</span></div>
                </template>
              </v-radio>
            </v-radio-group>
            <p>{{ $t('personInfo.merge.dataWarning') }}</p>
            <template v-if="accounts.length">
              <h3 class="subtitle-1">{{ $t('personInfo.merge.keepAccount') }}</h3>
              <v-alert v-if="accounts.length === 2" type="warning" text dense>{{ $t('personInfo.merge.twoAccounts') }}</v-alert>
              <v-radio-group v-model="accountId" class="mt-2">
                <v-radio v-for="entry in accounts" :key="entry.id" :value="entry.id">
                  <template v-slot:label><div class="merge-option"><strong>{{ entry.account.username || $t('table.tooltip.account_no-username') }}</strong><span>{{ name(entry) }} · UID {{ entry.account.uid }}</span></div></template>
                </v-radio>
              </v-radio-group>
            </template>
            <p v-else>{{ $t('personInfo.merge.noAccounts') }}</p>
          </template>
        </template>
        <template v-else>
          <v-row>
            <v-col cols="12" sm="6"><h3 class="subtitle-1 success--text">{{ $t('personInfo.merge.retained') }}</h3><p class="mb-1 font-weight-medium"><a :href="'/manage/edit/researchers/edit/' + preview.target.id" target="_blank" rel="noopener noreferrer">{{ name(preview.target) }} #{{ preview.target.id }}<v-icon x-small class="ml-1">mdi-open-in-new</v-icon></a></p><div class="merge-wrap">{{ preview.target.emails.join(', ') || $t('personInfo.noEmail') }}</div></v-col>
            <v-col cols="12" sm="6"><h3 class="subtitle-1 error--text">{{ $t('personInfo.merge.removed') }}</h3><p class="mb-1 font-weight-medium"><a :href="'/manage/edit/researchers/edit/' + preview.source.id" target="_blank" rel="noopener noreferrer">{{ name(preview.source) }} #{{ preview.source.id }}<v-icon x-small class="ml-1">mdi-open-in-new</v-icon></a></p><div class="merge-wrap">{{ preview.source.emails.join(', ') || $t('personInfo.noEmail') }}</div></v-col>
          </v-row>
          <v-divider class="my-4" />
          <v-expansion-panels v-if="preview.differences && preview.differences.length" v-model="differencesOpen" flat class="mb-4">
            <v-expansion-panel>
              <v-expansion-panel-header>{{ $t('personInfo.merge.differences') }} ({{ preview.differences.length }})</v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-for="difference in preview.differences" :key="difference.key" class="merge-difference">
                  <strong>{{ fieldLabel(difference) }}</strong>
                  <v-radio-group v-model="fieldChoices[difference.key]" hide-details class="mt-1">
                    <v-row>
                      <v-col v-for="option in [{ id: preview.target.id, person: preview.target, value: difference.kept }, { id: preview.source.id, person: preview.source, value: difference.removed }]" :key="option.id" cols="12" sm="6">
                        <v-radio :value="option.id" class="align-start">
                          <template v-slot:label><div class="merge-option"><span class="text-caption">{{ name(option.person) }} #{{ option.id }}</span><v-img v-if="difference.image && option.value" :src="mediaUrl(option.value)" :alt="name(option.person)" contain width="140" height="140" /><span v-else class="merge-field-value">{{ difference.sensitive ? $t('personInfo.merge.' + (option.value ? 'configured' : 'notConfigured')) : display(option.value) }}</span></div></template>
                        </v-radio>
                      </v-col>
                    </v-row>
                  </v-radio-group>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <p>{{ $t('personInfo.merge.accountResult') }}: <strong>{{ preview.kept_account ? (preview.kept_account.username || $t('table.tooltip.account_no-username')) : $t('personInfo.noAccount') }}</strong></p>
          <v-alert v-if="preview.discarded_account" type="warning" text dense>
            {{ $t('personInfo.merge.deletedAccount', { username: preview.discarded_account.username || $t('table.tooltip.account_no-username') }) }}
          </v-alert>
          <v-alert v-if="preview.discarded_staff_account" type="error" text dense>{{ $t('personInfo.merge.staffWarning') }}</v-alert>
          <p>{{ $t('personInfo.merge.servicesWarning') }}</p>
          <p v-if="preview.both_profiles">{{ $t('personInfo.merge.profileWarning') }}</p>
          <p class="font-weight-medium">{{ $t('personInfo.merge.relationsPreserved') }}</p>
          <v-checkbox v-model="acknowledged" :label="$t('personInfo.merge.acknowledge')" hide-details class="mt-4" />
        </template>
      </v-card-text>
      <v-card-actions class="merge-actions">
        <v-btn text :disabled="saving" @click="$emit('input', false)">{{ $t('button.cancel') }}</v-btn>
        <v-spacer />
        <v-btn v-if="preview" text :disabled="saving" @click="preview = null; acknowledged = false; error = ''">{{ $t('button.back') }}</v-btn>
        <v-btn v-if="!preview" color="primary" :loading="loading" :disabled="!other || !keepId || (accounts.length > 0 && !accountId)" @click="review">{{ $t('personInfo.merge.review') }}</v-btn>
        <v-btn v-else color="error" :loading="saving" :disabled="!acknowledged" @click="confirm">{{ $t('personInfo.merge.confirm') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';

export default {
  props: { value: Boolean, person: { type: Object, required: true }, people: { type: Array, default: () => [] }, initialOtherId: { type: Number, default: null } },
  data: () => ({ otherId: null, other: null, keepId: null, accountId: null, preview: null, loading: false, saving: false, acknowledged: false, error: '', requestId: 0, fieldChoices: {}, differencesOpen: 0 }),
  computed: {
    candidates() { return this.people.filter(entry => entry.id !== this.person.id).map(entry => ({ id: entry.id, label: [Object.values(entry.preferred_name || {}).join(' '), ...(entry.email || []), '#' + entry.id].filter(Boolean).join(' · ') })); },
    accounts() { return [this.person, this.other].filter(entry => entry && entry.account); }
  },
  watch: {
    value(open) {
      if (open) {
        this.reset();
        if (this.initialOtherId && this.initialOtherId !== this.person.id) {
          this.otherId = this.initialOtherId;
          this.loadOther();
        }
      }
    },
    fieldChoices: { deep: true, handler() { this.acknowledged = false; } }
  },
  methods: {
    mediaUrl(value) { return /^https?:\/\//.test(value) ? value : this.$store.state.endpoint.replace(/\/$/, '') + '/media/' + String(value).replace(/^\/?media\//, '').replace(/^\/+/, ''); },
    display(value) {
      if (value === null || value === undefined || value === '') return this.$t('personInfo.none');
      if (typeof value === 'boolean') return this.$t('personInfo.' + (value ? 'yes' : 'no'));
      if (Array.isArray(value)) return value.map(item => typeof item === 'object' ? JSON.stringify(item, null, 2) : item).join(', ') || this.$t('personInfo.none');
      if (typeof value === 'object') return JSON.stringify(value, null, 2);
      if (typeof value === 'string' && /<\/?[a-z][^>]*>/i.test(value)) return new DOMParser().parseFromString(value, 'text/html').body.textContent || this.$t('personInfo.none');
      return value;
    },
    fieldLabel(row) {
      const suffixes = { given_name: 'form.name', family_name: 'form.familyName', gl: 'language.galego', es: 'language.castellano', en: 'language.english', type: 'type' };
      return [this.$t('personInfo.scopes.' + row.scope), this.$t('personInfo.fields.' + row.field), ...(row.path || []).map(key => suffixes[key] ? this.$t(suffixes[key]) : key)].join(' · ');
    },
    name(entry) { return [entry.name.given_name, entry.name.family_name].filter(Boolean).join(' '); },
    endpoint(path) { return this.$store.state.endpoint + '/person/' + path; },
    reset() { this.requestId++; this.otherId = null; this.other = null; this.keepId = this.person.id; this.accountId = null; this.preview = null; this.error = ''; this.acknowledged = false; this.loading = false; this.fieldChoices = {}; this.differencesOpen = 0; },
    async loadOther() {
      const requestId = ++this.requestId;
      this.other = null; this.preview = null; this.error = ''; this.accountId = null;
      this.loading = false;
      if (!this.otherId) return;
      this.loading = true;
      try {
        const { data } = await axios.get(this.endpoint(this.otherId + '/overview'));
        if (requestId !== this.requestId) return;
        this.other = data;
        this.accountId = this.accounts.length === 1 ? this.accounts[0].id : null;
      } catch (error) { if (requestId === this.requestId) this.error = this.$t('personInfo.error'); }
      finally { if (requestId === this.requestId) this.loading = false; }
    },
    showError(error) {
      const reason = error.response && error.response.data && error.response.data.reason;
      const key = Array.isArray(reason) ? reason[0] : reason;
      return key && this.$te('personInfo.merge.errors.' + key) ? this.$t('personInfo.merge.errors.' + key) : this.$t('personInfo.merge.failed');
    },
    async review() {
      if (!this.other || this.loading) return;
      this.loading = true; this.error = ''; this.acknowledged = false;
      try {
        const { data } = await axios.post(this.endpoint('merge/preview'), { source: this.keepId === this.person.id ? this.other.id : this.person.id, target: this.keepId, account_person: this.accountId });
        this.preview = data;
        this.fieldChoices = Object.fromEntries((data.differences || []).map(row => [row.key, row.default_person]));
        this.differencesOpen = 0;
      } catch (error) { this.error = this.showError(error); }
      finally { this.loading = false; }
    },
    async confirm() {
      if (!this.preview || !this.acknowledged || this.saving) return;
      this.saving = true; this.error = '';
      try {
        const { data } = await axios.post(this.endpoint('merge/confirm'), { token: this.preview.token, confirmed: true, field_choices: this.fieldChoices });
        this.$emit('merged', data);
        this.$emit('input', false);
      } catch (error) { this.error = this.showError(error); }
      finally { this.saving = false; }
    }
  }
};
</script>

<style scoped>
.merge-option { display: flex; flex-direction: column; overflow-wrap: anywhere; padding: 5px 0; }
.merge-option span { font-size: 13px; }
.merge-wrap { overflow-wrap: anywhere; }
.merge-difference { padding: 10px 0; border-bottom: 1px solid #eee; overflow-wrap: anywhere; white-space: pre-wrap; }
.merge-field-value { max-height: 220px; overflow: auto; max-width: 100%; }
.merge-difference ::v-deep .v-label { align-items: flex-start; min-width: 0; max-width: calc(100% - 32px); }
.merge-actions { flex-wrap: wrap; gap: 6px; }
.merge-actions .v-btn { max-width: 100%; }
.merge-actions ::v-deep .v-btn__content { white-space: normal; }
</style>
